import Package from '../../../package.json';
import React, {Fragment, useState} from 'react';
import {Button, Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap';
import {useSelector, useDispatch} from 'react-redux';
import {NavLink, useNavigate} from 'react-router-dom';
import {getApiInformation, logout} from '../../slices/auth.slice';
import {toast} from 'react-toastify';
import Avatar from '../../components/Avatar';
import RoleBadge from '../../components/RoleBadge';
import DateTimeDisplay from '../../components/DateTimeDisplay';
import './Sidebar.scss';

const version = Package.version;

const SidebarShell = ({ isOpen, toggle, organization = null, variant = 'bac', children }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user, apiRoot } = useSelector((state) => state.auth);
    const { unreadCount } = useSelector((state) => state.notifications);
    const [versionModal, setVersionModal] = useState(false);
    const [helpModal, setHelpModal] = useState(false);

    const toggleVersionModal = () => setVersionModal(!versionModal);
    const toggleHelpModal = () => setHelpModal(!helpModal);

    if (!user) {
        return;
    }

    const handleLogout = () => {
        // The logout reducer clears every persisted key, impersonation included — a
        // hand-rolled list here would only drift out of date.
        dispatch(logout());
        navigate('/login');
        toast('Logged out successfully');
        window.location.reload();
    };

    const isOrgVariant = variant === 'org';

    return (
        <>
            {/* Backdrop overlay - only visible when open on mobile/tablet */}
            {isOpen && (
                <div className="sidebar-backdrop d-xl-none" onClick={toggle} />
            )}
            <div className={`sidebar sidebar-${variant} d-xl-block ${isOpen ? 'open' : ''}`}>
                <div className="sidebar-content position-relative h-100 d-flex flex-column">
                <header className={isOrgVariant ? 'my-4 mb-0 text-center' : 'pt-4 pb-3 text-center'}>
                    <img src="/logo.svg" alt="BEL AIR Cinema" width={isOrgVariant ? 112 : 90} className="pt-1" />

                    {isOrgVariant ? (
                        <Fragment>
                            <div className="d-flex align-items-start mt-4 px-4 py-3">
                                <NavLink to="/users/me" className="text-decoration-none" onClick={toggle}>
                                    <Avatar
                                        imageUrl={user.profilePic?.imageUrl}
                                        name={`${user.firstName} ${user.lastName}`}
                                        size={60}
                                    />
                                </NavLink>
                                <div className="ms-4 text-start flex-grow-1">
                                    <NavLink to="/users/me" className="text-decoration-none text-white" onClick={toggle}>
                                        <p className="mb-2 text-uppercase user-name">
                                            {user.firstName} {user.lastName}
                                        </p>
                                    </NavLink>
                                    {organization && (
                                        <p className="mb-0 small text-white-50">
                                            {organization.name}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="mb-3">
                                <RoleBadge role={user.role} />
                            </div>
                        </Fragment>
                    ) : (
                        <div className="profile-row d-flex align-items-center text-start mt-4 px-4">
                            <NavLink to="/users/me" className="text-decoration-none" onClick={toggle}>
                                <Avatar
                                    imageUrl={user.profilePic?.imageUrl}
                                    name={`${user.firstName} ${user.lastName}`}
                                    size={40}
                                />
                            </NavLink>
                            <div className="ms-3 flex-grow-1 overflow-hidden">
                                <NavLink to="/users/me" className="text-decoration-none text-white" onClick={toggle}>
                                    <div className="user-name text-uppercase text-truncate">
                                        {user.firstName} {user.lastName}
                                    </div>
                                </NavLink>
                                <div className="mt-1">
                                    <RoleBadge role={user.role} />
                                </div>
                            </div>
                        </div>
                    )}

                    <NavLink
                        to="#"
                        className="d-none d-xl-block text-decoration-none"
                        onClick={(e) => { e.preventDefault(); toggleHelpModal(); }}
                    >
                        <div className="help-icon position-absolute">
                            <span className="fas fa-question-circle p-2 text-primary border border-primary" />
                        </div>
                    </NavLink>

                    <NavLink to="/notifications" className="d-none d-xl-block text-decoration-none">
                        <div className="notifications position-absolute">
                            <span className={`fas fa-bell p-2 text-primary border border-primary ${unreadCount > 0 ? 'fa-shake' : ''}`}/>
                            {unreadCount > 0 && (
                                <span className="unread bg-danger position-absolute small">
                                {unreadCount < 10 ? unreadCount : '9+'}
                            </span>
                            )}
                        </div>
                    </NavLink>

                </header>
                <div className="nav-scroll">
                    {children}
                </div>

                <div className="sidebar-footer text-center mt-auto">
                    <Button
                        color="link"
                        className="text-decoration-none text-white"
                        onClick={toggleVersionModal}
                    >
                        <span className="version"><strong>v{version}</strong> | <DateTimeDisplay/></span>
                    </Button>
                    <br/>
                    <Button
                        color="link"
                        className="logout text-white text-decoration-none"
                        onClick={handleLogout}
                    >
                        <span className="fas fa-sign-out me-2"/>
                        LOGOUT
                    </Button>
                    {process.env.REACT_APP_ENVIRONMENT === 'development' && (
                        <div className="bg-danger text-center">
                            {process.env.REACT_APP_ENVIRONMENT.toUpperCase()}
                        </div>
                    )}
                    {process.env.REACT_APP_ENVIRONMENT === 'staging' && (
                        <div className="bg-warning text-center">
                            {process.env.REACT_APP_ENVIRONMENT.toUpperCase()}
                        </div>
                    )}
                </div>

                <Modal isOpen={helpModal}>
                    <ModalHeader toggle={toggleHelpModal} tag="h1">
                        Support
                    </ModalHeader>
                    <ModalBody>
                        <div className="d-flex flex-column gap-3">
                            <div>
                                <a href="https://www.belaircinema.com/" target="_blank" rel="noopener noreferrer" className="fw-bold fs-6 text-primary text-decoration-none">
                                    Bel Air Cinema <span className="fas fa-external-link-alt small ms-1" />
                                </a>
                                <p className="text-white small mb-0 mt-1">
                                    Visit the official Bel Air Cinema website for news, updates, and general information about our platform.
                                </p>
                            </div>
                            <hr className="my-0" />
                            <div>
                                <a href="https://www.belaircinema.com/register" target="_blank" rel="noopener noreferrer" className="fw-bold fs-6 text-primary text-decoration-none">
                                    Register <span className="fas fa-external-link-alt small ms-1" />
                                </a>
                                <p className="text-white small mb-0 mt-1">
                                    Create a new account to get access to BAC support.
                                </p>
                            </div>
                            <hr className="my-0" />
                            <div>
                                <a href="https://www.belaircinema.com/bac-login" target="_blank" rel="noopener noreferrer" className="fw-bold fs-6 text-primary text-decoration-none">
                                    BAC Login <span className="fas fa-external-link-alt small ms-1" />
                                </a>
                                <p className="text-white small mb-0 mt-1">
                                    Access the BAC portal directly to access support pages.
                                </p>
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" onClick={toggleHelpModal}>Close</Button>
                    </ModalFooter>
                </Modal>

                <Modal isOpen={versionModal}>
                    <ModalHeader toggle={toggleVersionModal} tag="h1">
                        BEL AIR CINEMA
                    </ModalHeader>
                    <ModalBody>
                        <h4 className="text-primary text-uppercase">Frontend version</h4>
                        <p>v{version}</p>

                        <h4 className="text-primary text-uppercase">Backend Information</h4>
                        <p>
                            <strong>Version:</strong> {apiRoot?.version}<br/>
                            <strong>Uptime:</strong> {apiRoot?.uptime}<br/>
                            <strong>WAN:</strong> {apiRoot?.internetConnectivity ? 'Connected' : 'Disconnected'}<br/>
                            <strong>Last WAN state change:</strong> {apiRoot?.lastConnectivityUpdated ? new Date(apiRoot.lastConnectivityUpdated).toLocaleString() : 'N/A' }<br/>
                        </p>

                        {organization && (
                            <Fragment>
                                <h4 className="text-primary text-uppercase">Cronjob version</h4>
                                <p>
                                    {organization?.lastCronjobVersion || 'No Cronjob information'}<br/>
                                    Last synced: {organization.lastSynced}
                                </p>
                            </Fragment>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" outline onClick={() => dispatch(getApiInformation())}>
                            <span className="fas fa-refresh me-2" /> Refresh version
                        </Button>
                        <Button color="primary" onClick={toggleVersionModal}>Close</Button>
                    </ModalFooter>
                </Modal>
                </div>
            </div>
        </>
    );
};

export default SidebarShell;
